import React from 'react';
import TopNavigation from '../TopNavigation';

/**
 * One job costs the company $85,524 all in. Every scenario below splits that same
 * $85,524 between the driver, the government, the robot's vendor and the company,
 * so all four columns foot to the cost of the position.
 */
const POSITION_COST = 85524;

const PARTY = {
  driver: { label: 'The driver', color: '#2a78d6' },
  government: { label: 'Government', color: '#eb6834' },
  company: { label: 'The company', color: '#1baf7a' },
  robot: { label: 'The robot', color: '#8a867d' }
} as const;

type Party = keyof typeof PARTY;

const ALLOCATIONS: Array<{ name: string; note: string; parts: Array<{ who: Party; amount: number }> }> = [
  {
    name: 'Today. A person drives.',
    note: 'The company pays a salary. The driver and the government split it.',
    parts: [
      { who: 'driver', amount: 60543 },
      { who: 'government', amount: 24981 }
    ]
  },
  {
    name: 'A robot drives. Nobody changes the tax code.',
    note: 'Corporate tax on the saved wage is all the government sees.',
    parts: [
      { who: 'government', amount: 21604 },
      { who: 'robot', amount: 4000 },
      { who: 'company', amount: 59920 }
    ]
  },
  {
    name: 'A robot drives, taxed enough to break even.',
    note: 'A $3,377 AI tax puts the government back where it started.',
    parts: [
      { who: 'government', amount: 24981 },
      { who: 'robot', amount: 4000 },
      { who: 'company', amount: 56543 }
    ]
  },
  {
    name: 'A robot drives, and the driver keeps a day.',
    note: 'A $19,377 AI tax covers the government and buys the driver one day a week.',
    parts: [
      { who: 'driver', amount: 16000 },
      { who: 'government', amount: 24981 },
      { who: 'robot', amount: 4000 },
      { who: 'company', amount: 40543 }
    ]
  }
];

const money = (n: number) => '$' + n.toLocaleString('en-CA');
const share = (n: number) => (n / POSITION_COST) * 100;

const HowToWinWithAIBlog: React.FC = () => {
  return (
    <>
      <style>{`
        .blog-page {
          font-family: 'Georgia', serif;
          background: #fafaf8;
          color: #1a1a18;
          line-height: 1.85;
          min-height: 100vh;
        }
        .blog-content {
          max-width: 740px;
          margin: 0 auto;
          padding: 3rem 1.5rem 5rem;
        }
        .blog-kicker {
          font-family: sans-serif;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #888;
          margin-bottom: 0.75rem;
        }
        .blog-content h1 {
          font-family: 'Georgia', serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1rem;
          color: #111;
        }
        .blog-meta {
          font-family: sans-serif;
          font-size: 12px;
          color: #999;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e8e6e0;
          margin-bottom: 1.75rem;
        }
        .blog-lede {
          font-size: 19px;
          font-weight: 300;
          font-style: italic;
          line-height: 1.7;
          margin-bottom: 1.5rem;
          color: #222;
        }
        .blog-content p {
          font-size: 16px;
          font-weight: 300;
          line-height: 1.9;
          margin-bottom: 1.2rem;
          color: #1a1a18;
        }
        .blog-content p a, .blog-steptext a, .blog-sources a {
          color: #1a1a18;
          text-decoration: underline;
          text-decoration-color: #bbb;
        }
        .blog-content h2 {
          font-family: 'Georgia', serif;
          font-size: 26px;
          font-weight: 700;
          margin: 2.75rem 0 0.9rem;
          line-height: 1.3;
          color: #111;
        }
        .blog-content h3 {
          font-family: sans-serif;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.09em;
          color: #999;
          margin: 2.25rem 0 0.6rem;
        }
        .blog-content hr {
          border: none;
          border-top: 1px solid #e8e6e0;
          margin: 2.25rem 0;
        }
        .blog-pq {
          border-left: 3px solid #ccc;
          padding: 0.6rem 1.5rem;
          margin: 2rem 0;
          font-family: 'Georgia', serif;
          font-size: 20px;
          font-style: italic;
          line-height: 1.55;
          color: #333;
        }
        .blog-numgrid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin: 1.5rem 0;
        }
        .blog-numbox {
          background: #f5f3ee;
          border-radius: 8px;
          padding: 1rem 1.1rem;
        }
        .blog-bignum {
          font-family: 'Georgia', serif;
          font-size: 30px;
          font-weight: 700;
          line-height: 1;
          color: #111;
        }
        .blog-bignumlabel {
          font-family: sans-serif;
          font-size: 12px;
          color: #777;
          margin-top: 5px;
          line-height: 1.45;
        }
        .blog-table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
          font-family: sans-serif;
          font-size: 13px;
        }
        .blog-table th {
          text-align: left;
          font-weight: 500;
          color: #555;
          border-bottom: 2px solid #e8e6e0;
          padding: 8px 12px 8px 0;
        }
        .blog-table td {
          padding: 9px 12px 9px 0;
          border-bottom: 1px solid #f0ede6;
          color: #333;
          vertical-align: top;
        }
        .blog-table tr:last-child td { border-bottom: none; }
        .blog-steps { margin: 1.5rem 0; }
        .blog-step { display: flex; gap: 14px; margin-bottom: 1.1rem; align-items: flex-start; }
        .blog-stepnum {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border-radius: 50%;
          background: #f0ede6;
          border: 1px solid #ddd;
          display: flex; align-items: center; justify-content: center;
          font-family: sans-serif;
          font-size: 13px; font-weight: 500;
          margin-top: 3px;
          color: #444;
        }
        .blog-steptext { font-size: 15.5px; font-weight: 300; line-height: 1.8; }
        .blog-steptext strong { font-weight: 500; }
        .blog-alloc { margin: 1.75rem 0 1.5rem; }
        .blog-alloc-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-family: sans-serif;
          font-size: 12px;
          color: #666;
          margin-bottom: 1.4rem;
        }
        .blog-alloc-legend span { display: flex; align-items: center; gap: 6px; }
        .blog-alloc-legend i { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
        .blog-alloc-row { margin-bottom: 1.35rem; }
        .blog-alloc-name {
          font-family: sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          color: #333;
          margin-bottom: 6px;
        }
        .blog-alloc-bar { display: flex; gap: 2px; height: 26px; }
        .blog-alloc-seg { border-radius: 2px; }
        .blog-alloc-seg:first-child { border-radius: 4px 2px 2px 4px; }
        .blog-alloc-seg:last-child { border-radius: 2px 4px 4px 2px; }
        .blog-alloc-labels {
          display: flex;
          gap: 2px;
          margin-top: 5px;
          font-family: sans-serif;
          font-size: 11px;
          color: #777;
        }
        .blog-alloc-labels span { overflow: hidden; white-space: nowrap; }
        .blog-alloc-note {
          font-family: sans-serif;
          font-size: 11.5px;
          color: #999;
          margin-top: 5px;
        }
        .blog-alloc-list {
          display: none;
          flex-wrap: wrap;
          gap: 6px 14px;
          margin-top: 6px;
          font-family: sans-serif;
          font-size: 11.5px;
          color: #777;
        }
        .blog-alloc-list span { display: flex; align-items: center; gap: 5px; }
        .blog-alloc-list i { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
        @media (max-width: 620px) {
          .blog-alloc-labels { display: none; }
          .blog-alloc-list { display: flex; }
        }
        .blog-tuesday {
          display: block;
          margin-top: 4px;
          font-family: sans-serif;
          font-size: 12.5px;
          color: #777;
        }
        .blog-sources {
          font-family: sans-serif;
          font-size: 12px;
          line-height: 1.7;
          color: #777;
          padding-left: 1.1rem;
          margin: 0.5rem 0 0;
        }
        .blog-sources li { margin-bottom: 3px; }
        .blog-byline {
          font-family: sans-serif;
          font-size: 12px;
          color: #999;
          font-style: italic;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e8e6e0;
        }
        .blog-disc {
          font-family: sans-serif;
          font-size: 11px;
          color: #aaa;
          line-height: 1.65;
          margin-top: 1.5rem;
        }
        .blog-disc strong { color: #888; }
        @media (max-width: 540px) {
          .blog-numgrid { grid-template-columns: 1fr; }
        }
      `}</style>

      <TopNavigation onLogin={() => {}} user={null} onLogout={() => {}} />

      <div className="blog-page">
        <div className="blog-content">

          <a href="/blog" style={{display:'inline-block',marginBottom:'1.5rem',fontFamily:'sans-serif',fontSize:'13px',color:'#888',textDecoration:'none'}}>← All Posts</a>

          <div className="blog-kicker">Fiscal Policy · AI</div>
          <h1>How to Win With AI</h1>
          <div className="blog-meta">LarrysHelp.org &nbsp;·&nbsp; September 2026 &nbsp;·&nbsp; 11 min read</div>

          <p className="blog-lede">Here is the story we're fed: AI takes every job, and the people who built it put the odds of it ending humanity somewhere between five and twenty percent. Fine. Take that at face value. Here is how Canada wins anyway, and what you could be doing on a Tuesday afternoon.</p>

          <h2>The Story We're Fed</h2>

          <div className="blog-numgrid">
            <div className="blog-numbox">
              <div className="blog-bignum">71%</div>
              <div className="blog-bignumlabel">Americans who think AI will reduce jobs. Only 5% think it will create more.</div>
            </div>
            <div className="blog-numbox">
              <div className="blog-bignum">3%</div>
              <div className="blog-bignumlabel">Canadians who think AI will significantly increase employment. 45% expect major losses.</div>
            </div>
            <div className="blog-numbox">
              <div className="blog-bignum">5%</div>
              <div className="blog-bignumlabel">Median odds of an extinction-level outcome, from a survey of 2,778 AI researchers. The mean was 16%.</div>
            </div>
          </div>

          <p>Geoffrey Hinton, who won a Nobel for the work that made this possible, puts it at 10 to 20 percent within 30 years. Nobody serious calls it zero.</p>

          <p>So we won't argue with the story. We'll do what we do with every headline number on this site: follow the money and see where it actually lands.</p>

          <hr />

          <h2>Fine. Assume the Jobs Go.</h2>

          <p>Start with the job everyone agrees goes first: driving. A study of US and European trucking found 3.4 to 4.4 million driver jobs become redundant by 2030 if driverless trucks roll out quickly. The reason isn't mysterious. The driver is a fleet's single biggest operating cost, ahead of fuel.</p>

          <p>Now list what we get in exchange, because the story we're fed never does:</p>

          <div className="blog-steps">
            <div className="blog-step">
              <div className="blog-stepnum">1</div>
              <div className="blog-steptext"><strong>Fewer accidents and deaths.</strong> Across 170 million fully autonomous miles, Waymo's driver was in 92 percent fewer crashes causing serious or fatal injury than humans on the same roads.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">2</div>
              <div className="blog-steptext"><strong>An hour a day back.</strong> The average Canadian commuter spends 63 minutes a day getting to and from work. That hour returns.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">3</div>
              <div className="blog-steptext"><strong>Nobody chauffeurs.</strong> Teens get to practice without a parent driving. Parents stop being the taxi and start being part of the backseat gang.</div>
            </div>
          </div>

          <p>That's the upside. Now the honest downside, in one driver's numbers.</p>

          <hr />

          <h2>One Driver, Four Ways</h2>

          <p>Take an Ontario long-haul driver on $80,000. The salary isn't what the job costs. Add the employer's share of CPP and EI and the position costs the company <strong>$85,524</strong>. That's the number to start from, because it doesn't change in any scenario below. What changes is who ends up holding it.</p>

          <p>We ran the driver through our own <a href="/income-tax">income tax calculator</a> at 2026 Ontario rates, then replaced them with a robot that does the job for one-twentieth the cost, $4,000 a year. The robot price is a utopia assumption and we'll flag it as one, but it's the same assumption the doom story makes.</p>

          <div className="blog-alloc">
            <div className="blog-alloc-legend">
              {(Object.keys(PARTY) as Party[]).map(key => (
                <span key={key}><i style={{ background: PARTY[key].color }} />{PARTY[key].label}</span>
              ))}
            </div>

            {ALLOCATIONS.map(scenario => (
              <div className="blog-alloc-row" key={scenario.name}>
                <div className="blog-alloc-name">{scenario.name}</div>
                <div
                  className="blog-alloc-bar"
                  role="img"
                  aria-label={`${scenario.name} Of ${money(POSITION_COST)}: ` +
                    scenario.parts.map(part => `${PARTY[part.who].label} ${money(part.amount)}`).join(', ') + '.'}
                >
                  {scenario.parts.map(part => (
                    <div
                      key={part.who}
                      className="blog-alloc-seg"
                      style={{ width: `${share(part.amount)}%`, background: PARTY[part.who].color }}
                      title={`${PARTY[part.who].label}: ${money(part.amount)}`}
                    />
                  ))}
                </div>
                <div className="blog-alloc-labels" aria-hidden="true">
                  {scenario.parts.map(part => (
                    <span key={part.who} style={{ width: `${share(part.amount)}%` }}>
                      {share(part.amount) >= 14 ? money(part.amount) : ''}
                    </span>
                  ))}
                </div>
                <div className="blog-alloc-list" aria-hidden="true">
                  {scenario.parts.map(part => (
                    <span key={part.who}>
                      <i style={{ background: PARTY[part.who].color }} />{money(part.amount)}
                    </span>
                  ))}
                </div>
                <div className="blog-alloc-note">{scenario.note}</div>
              </div>
            ))}
          </div>

          <p>Every bar is the same length, because every bar is the same $85,524. Read them as one question: when the job gets done by a machine, where does the money that used to be a salary actually land?</p>

          <h3>Where the government's money comes from</h3>

          <div style={{ overflowX: 'auto' }}>
            <table className="blog-table">
              <thead>
                <tr>
                  <th></th>
                  <th>A person drives</th>
                  <th>A robot drives</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Federal and Ontario income tax</td>
                  <td>$14,352</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>CPP and EI, both sides</td>
                  <td>$10,629</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>Corporate tax on the saved wage</td>
                  <td>—</td>
                  <td>$21,604</td>
                </tr>
                <tr>
                  <td><strong>Total</strong></td>
                  <td><strong>$24,981</strong></td>
                  <td><strong>$21,604</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="blog-pq">"The driver's $60,543 doesn't move to the government. It moves to the company."</div>

          <p>That gap isn't an accident. Daron Acemoglu and his colleagues at MIT showed the US tax code taxes labour at roughly 25 percent and capital, including software and robots, at about 5 percent. The code nudges firms toward the machine, then collects less when they take the nudge. Canada's code is built the same way.</p>

          <p>The government's own hole is small, and worth being honest about: $24,981 becomes $21,604, so it's short $3,377 per driver. Charge the robot that, and the third bar balances. The government is whole and the company is still $56,543 ahead. Everyone wins except the person the job belonged to, whose bar is now empty. Which raises the obvious question. If we're setting the rate anyway, why stop at whole?</p>

          <h3>Column four: pay the driver</h3>

          <p>Charge the robot $19,377 instead. The government still keeps its $24,981, exactly as before, and the other $16,000 goes to the driver. That's 20 percent of the old salary, tax-free. Tuesday is theirs.</p>

          <p>The driver is not made whole and we won't pretend otherwise. Four days still need filling, and they'll be filled with something else. But the fifth day is paid, and it's the day that was never coming back any other way. The company still clears $40,543 on the position. The government doesn't lose a cent. Nobody's income tax went up.</p>

          <p>Call it what it is: a guaranteed basic income, funded by the machine that took the job, priced at some share of the salary it replaced. Twenty percent is just the number that buys a day. The real point is the difference between a policy that balances a budget and a policy somebody actually wants.</p>

          <hr />

          <h2>How Society Wins</h2>

          <p>Six moves. Each one ends with what a Tuesday afternoon could look like, because a plan that ends at "income support" is a plan for people to sleep more.</p>

          <div className="blog-steps">
            <div className="blog-step">
              <div className="blog-stepnum">1</div>
              <div className="blog-steptext"><strong>The robot drives.</strong> Covered above: 92 percent fewer serious crashes and an hour a day back.
                <span className="blog-tuesday">Tuesday: the rink instead of the 401.</span></div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">2</div>
              <div className="blog-steptext"><strong>The robot pays the tax.</strong> Bill Gates revived his robot tax in August 2026 and added a tax on AI tokens. Two Canadian senators, Kim Pate and Yuen Pau Woo, proposed an AI Dividend Fund financed by a small charge on every API call or robot task, scaled to the taxes the displaced worker would have paid. Here's the part nobody says out loud: HST is already robot-proof. When a robot-made product is sold, HST is collected regardless of who made it. Only income and payroll tax vanish with the worker. A token tax is just HST on robot work.
                <span className="blog-tuesday">Tuesday: the gap closes, and no paycheque paid for it.</span></div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">3</div>
              <div className="blog-steptext"><strong>Stop taxing work. All of it.</strong> Personal income tax brought Ottawa $234 billion last year, 46 percent of everything it collects, and it dies with the paycheque. The cautious fix is Acemoglu's: drop labour to about 18 percent and raise the return on automation to about 27. The braver fix is zero on labour. Tax interest, dividends, capital gains and robot work, and let every hour a human works come home untaxed. That sounds insane until you look at the incentive we run today: tax the human ref at 25 percent while the machine that replaces him is taxed at 5, and you have built the exact nudge you claim you want to stop. Zero on labour is a big enough idea to need its own article. This one only argues that the tax on work has an expiry date.
                <span className="blog-tuesday">Tuesday: the ref keeps the whole cheque, because nobody taxes the whistle.</span></div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">4</div>
              <div className="blog-steptext"><strong>Every day is Saturday.</strong> Nobody has to invent a post-work culture. It already exists two days a week. Seniors are the only Canadians living it full time, and nearly 80 percent say they're highly satisfied with how they use their time, against 62 percent of everyone else. The infrastructure is built too, and half empty. Halton Hills charges $299.79 an hour for prime ice and $168.66 for weekday daytime, because weekday daytime sits vacant. Canada has 965 curling clubs. Spread the demand across seven days and the same rinks serve the same programs before anyone pours concrete.
                <span className="blog-tuesday">Tuesday: the 1 pm curling draw, about $27 a game.</span></div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">5</div>
              <div className="blog-steptext"><strong>Health is the return.</strong> Canada spent an estimated $399 billion on health care in 2025, $9,626 per person, 12.7 percent of GDP. Physical inactivity alone costs the system $3.9 billion a year. Loneliness raises the risk of death by 26 to 29 percent, on par with 15 cigarettes a day. Social prescribing, where a doctor prescribes a club instead of a pill, returns $4.43 for every dollar in Canada. Everything in move four is a health intervention wearing a hockey jersey.
                <span className="blog-tuesday">Tuesday: a smaller line in every provincial budget.</span></div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">6</div>
              <div className="blog-steptext"><strong>Humans keep the whistle.</strong> Gates calls them "Human Reserved" jobs: work society keeps human on purpose, the way we keep nature reserves. Pro sports already ran the experiment the other way. Wimbledon cut all 300 line judges in 2025 after 147 years. MLB's robot strike zone went league-wide in 2026 and 72 percent of fans call it a positive. You can't win "real ref" on accuracy. You win it on purpose: pro sports sell accuracy, community sports sell participation. The bad call is part of beer league. And the rule fixes a crisis we already have. Hockey Canada's officials fell from 33,000 before the pandemic to 16,000 after, it loses about one in three every year, and Northern Ontario cancelled roughly 10 percent of its games for lack of refs. Reffing collapsed because it's a teenager's $30 gig with abuse attached. Move three makes it a career.
                <span className="blog-tuesday">Tuesday: a grown adult in stripes, paid properly, keeping the whole cheque.</span></div>
            </div>
          </div>

          <div className="blog-pq">"Let's keep the jobs that keep us human."</div>

          <hr />

          <h2>How You Win</h2>

          <p>You don't have to wait for Ottawa. Every move above has a personal version, and they share one test: <strong>use the tools that end with you outside.</strong> A dating app wins when you stay in it. A league schedule wins when you show up. Ask that of anything on your phone.</p>

          <div className="blog-steps">
            <div className="blog-step">
              <div className="blog-stepnum">1</div>
              <div className="blog-steptext"><strong>Delete the dating app, join the league.</strong> More than 75 percent of Gen Z report dating-app burnout. Tinder's paying users have fallen five quarters running. Run club membership grew 59 percent in a year, and one in five runners has dated someone from the club. In person is already winning. You're just early.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">2</div>
              <div className="blog-steptext"><strong>Take the class for the recital, not the résumé.</strong> Piano, pottery, a language. In this economy the point of learning is the Tuesday night, not the job posting.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">3</div>
              <div className="blog-steptext"><strong>Get certified in the thing you'd do anyway.</strong> Not a career pivot. Ref the sport you already watch, coach the one you played, teach the instrument already in your house. Officials, coaches and instructors are badly short right now, so the thing you'd happily do for free pays this season, and it sits on the Human Reserved list next decade.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">4</div>
              <div className="blog-steptext"><strong>Spend the time it saves you on something that isn't a screen.</strong> This is the one that gets away from people. Every hour AI hands back is an hour it would happily take again, in an app, reading about AI. The sixty-three minutes of commuting is the obvious hour to claim, and so is whatever tonight's software just saved you. Put it into a paper book, the phone call you owe someone, an earlier bedtime, a hand of cards. The saving only counts if you spend it on something you'd actually tell someone about.</div>
            </div>
            <div className="blog-step">
              <div className="blog-stepnum">5</div>
              <div className="blog-steptext"><strong>Go to the fair. Better, help run it.</strong> Play On! has run 182 street hockey events since 2002 with 2.5 million participants, on nothing more than a closed street and some nets. Your curling club runs learn-to-curl for about $150. Somebody has to fill those, and somebody has to run them.</div>
            </div>
          </div>

          <hr />

          <h2>Where AI Fits</h2>

          <p>It isn't leaving. So the question was never whether to use it, only where to point it. There is a clean line available, and it's worth drawing on purpose.</p>

          <p><strong>AI takes the before and the after. People keep the during.</strong> Everything that happens before people arrive and after they leave is logistics, and logistics is what software is for. The two hours in the middle are the product, and software has no business inside them.</p>

          <p>So hand over the scheduling. Ten people free the same night. The ice that's genuinely open. The sub at 5pm, found by pinging the dozen players whose history says they're free on a Tuesday and live ten minutes from the rink, instead of a captain texting thirty people one at a time and hearing back from four. Hand over the tasks that kill volunteer clubs: the books, the receipts, the grant form, the insurance renewal. Hand over the scoresheet and the clock, but never the whistle. Hand over drills and a season plan for the parent who has never coached. Hand over the rules of curling at 10pm the night before, so you walk in Tuesday like you belong. A doctor writes "this person needs people," and the software finds the league, the night, and the ride.</p>

          <p>What it never takes: the fourth on the team, the coach's face, the ref, the friend across the table. If the point of the moment is that another human is in the room, software is not a substitute for them. It is the reason they could both make it.</p>

          <p>One catch. The industry's default metric is engagement, which means minutes inside the app. Nobody builds get-you-outside software unless customers ask for it. So ask for it.</p>

          <hr />

          <p>It's worth being honest about what some of us are actually frightened of losing: a Tuesday morning Teams call. Trade it for a Tuesday worth keeping.</p>

          <div className="blog-byline">
            Written by the team at LarrysHelp.org.
          </div>

          <div className="blog-disc">
            <strong>Disclaimer:</strong> Income tax, CPP and EI figures for the $80,000 example come from the Larry's Help income tax calculator at 2026 Ontario rates. Employer CPP matches the employee amount and employer EI is 1.4 times the employee premium. The cost of the position is the $80,000 salary plus the employer's CPP and EI. Sales tax on what anyone later spends is excluded, so every scenario is a clean split of that one figure. A robot costing one-twentieth of a salary is a scenario assumption, not a forecast. Corporate tax uses Ontario's combined general rate of 26.5 percent and assumes the profit is taxed in Canada. The AI tax is modelled as a levy on top of corporate tax rather than a deductible expense, and the $16,000 paid to the driver is assumed tax-free in their hands. All figures illustrative. Educational analysis only, not financial or tax advice.
          </div>

          <h3>Sources</h3>
          <ul className="blog-sources">
            <li><a href="https://fortune.com/2026/08/26/bill-gates-robot-ai-tax-replacehuman-workers/">Fortune: Bill Gates wants to tax robots and AI tokens (Pew job-loss figures)</a></li>
            <li><a href="https://www.policyalternatives.ca/news-research/is-canadian-labour-ready-for-the-ai-invasion/">CCPA: Is Canadian labour ready for the AI invasion?</a></li>
            <li><a href="https://en.wikipedia.org/wiki/P(doom)">AI Impacts 2023 researcher survey and Hinton estimate, via P(doom)</a></li>
            <li><a href="https://www.fleetowner.com/technology/article/21696130/report-driverless-trucks-will-eliminate-millions-of-jobs">FleetOwner: Driverless trucks will eliminate millions of jobs</a></li>
            <li><a href="https://waymo.com/safety/impact/">Waymo Safety Impact</a></li>
            <li><a href="https://www150.statcan.gc.ca/n1/daily-quotidien/240605/dq240605a-eng.htm">StatCan: Time use and teleworkers, 2022 (commute time)</a></li>
            <li><a href="https://www.canada.ca/en/department-finance/services/publications/annual-financial-report/2025.html">Annual Financial Report of the Government of Canada 2024-25</a></li>
            <li><a href="https://mitsloan.mit.edu/ideas-made-to-matter/how-u-s-tax-code-promotes-excessive-automation">MIT Sloan: How the US tax code promotes excessive automation</a></li>
            <li><a href="https://www.policymagazine.ca/how-ai-can-help-boost-wealth-and-share-it-more-fairly/">Policy Magazine: The AI Dividend Fund proposal</a></li>
            <li><a href="https://arxiv.org/abs/2603.04555">arXiv: Token taxes can mitigate AI's economic risks</a></li>
            <li><a href="https://www150.statcan.gc.ca/n1/daily-quotidien/260319/dq260319c-eng.htm">StatCan: Satisfaction with time use, 2022</a></li>
            <li><a href="https://haltonhills.ic12.esolg.ca/en/explore-and-play/resources/Documents/2025-26-IceRates.pdf">Halton Hills 2025-26 arena rates</a></li>
            <li><a href="https://en.wikipedia.org/wiki/Curling_in_Canada">Curling in Canada: club and participant counts</a></li>
            <li><a href="https://www.cihi.ca/en/national-health-expenditure-trends/nhex-trends-reports/nhex-trends-2025-snapshot">CIHI: National health expenditure trends 2025</a></li>
            <li><a href="https://www.participaction.com/about/advocacy/">ParticipACTION: cost of physical inactivity</a></li>
            <li><a href="https://www.socialprescribing.ca/a-healthier-canada">Canadian Institute for Social Prescribing: Economic and Social Impact Report</a></li>
            <li><a href="https://bcmj.org/premise/social-prescribing-loneliness-epidemic">BC Medical Journal: Social prescribing for the loneliness epidemic</a></li>
            <li><a href="https://techxplore.com/news/2025-07-line-wimbledon-ai-jobs.html">TechXplore: Line judges missed at Wimbledon as AI takes their jobs</a></li>
            <li><a href="https://www.mlb.com/news/abs-challenge-system-mlb-2026">MLB: ABS Challenge System for 2026</a></li>
            <li><a href="https://sirc.ca/articles/theyre-not-yelling-at-you-theyre-yelling-at-your-shirt-canadian-sport-system-faces-officiating-crisis/">SIRC: Canadian sport system faces officiating crisis</a></li>
            <li><a href="https://columbianewsservice.com/2026/03/02/gen-z-is-logging-off-dating-apps-and-looking-for-love-irl/">Columbia News Service: Gen Z is logging off dating apps</a></li>
            <li><a href="https://grass.camp/en-US/blog/run-clubs-replacing-dating-apps-2026">Run clubs are the new dating apps: 2026 trend data</a></li>
            <li><a href="https://www.playon.ca/">Play On! Canada</a></li>
            <li><a href="https://actoncurlingclub.com/index.php/curling/membership-information">Acton Curling Club: learn-to-curl pricing</a></li>
            <li><a href="https://moores.samaltman.com/">Sam Altman: Moore's Law for Everything</a></li>
            <li><a href="https://lenispooner.substack.com/p/who-pays-when-ai-comes-for-canadian">Leni Spooner: Who pays when AI comes for Canadian jobs? (debt service figures)</a></li>
          </ul>

        </div>
      </div>
    </>
  );
};

export default HowToWinWithAIBlog;
